import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ListSearchBar from "@/components/ListSearchBar";
import { useDebounced } from "@/hooks/usePagedSearch";

export default function Onboarding() {
  const [vendors, setVendors] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [search, setSearch] = useState("");
  const q = useDebounced(search);

  // Searched on the server (name, category/industry, email, phone).
  const load = () => {
    const params = q ? { q } : {};
    api.get("/staff/vendor-queue", { params }).then((r) => setVendors(r.data));
    api.get("/staff/contractor-signups", { params }).then((r) => setContractors(r.data));
  };
  useEffect(() => { load(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (type, id, action) => {
    const { data } = await api.post(`/staff/${type === "vendor" ? "vendor-queue" : "contractor-signups"}/${id}/${action}`);
    toast.success(action === "approve" ? (data?.emailed ? `Approved — invitation emailed to ${data.emailed}` : "Approved") : "Declined");
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="onboarding-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Onboarding Queue</h1>
      <p className="text-slate-500 text-sm mt-1">Review pending vendor and contractor sign-ups.</p>
      <ListSearchBar value={search} onChange={setSearch} placeholder="Search pending sign-ups by name, category, email, phone…" testId="onboarding-search" />

      <Tabs defaultValue="vendors" className="mt-6">
        <TabsList>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors ({vendors.length})</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="tab-contractors">Contractors ({contractors.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="vendors" className="mt-5 space-y-3">
          {vendors.map((v) => (
            <div key={v.id} className="flex items-center gap-4 border border-slate-200 rounded-xl p-4" data-testid={`vendor-queue-row-${v.id}`}>
              <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center font-bold text-[12px] flex-shrink-0">{v.name.slice(0, 2).toUpperCase()}</div>
              <div className="flex-1">
                <div className="font-semibold text-[14px]">
                  {v.name}
                  {v.is_invitation && <span className="ml-2 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10.5px] font-semibold" data-testid={`invitation-badge-${v.id}`}>Invited by a contractor</span>}
                </div>
                <div className="text-[12px] text-slate-400">{v.category ? `${v.category} · ` : ""}{v.contact_email}</div>
                {v.is_invitation && (
                  <div className="text-[12px] text-slate-500 mt-0.5" data-testid={`invitation-detail-${v.id}`}>
                    Invited by {v.invited_by_name || "a team member"}{v.referred_by_company_name ? ` at ${v.referred_by_company_name}` : ""}
                    {v.invite_contact_name ? ` · contact ${v.invite_contact_name}` : ""}. Approving adds them to that contractor's network and emails the vendor to set up their portal.
                  </div>
                )}
              </div>
              <button onClick={() => decide("vendor", v.id, "approve")} data-testid={`approve-vendor-${v.id}`} className="px-3.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[12.5px] font-semibold">{v.is_invitation ? "Approve & email vendor" : "Approve"}</button>
              <button onClick={() => decide("vendor", v.id, "decline")} data-testid={`decline-vendor-${v.id}`} className="px-3.5 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Decline</button>
            </div>
          ))}
          {vendors.length === 0 && <div className="text-slate-400 text-sm text-center py-10">No vendors pending approval.</div>}
        </TabsContent>
        <TabsContent value="contractors" className="mt-5 space-y-3">
          {contractors.map((c) => (
            <div key={c.id} className="flex items-center gap-4 border border-slate-200 rounded-xl p-4" data-testid={`contractor-queue-row-${c.id}`}>
              <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center font-bold text-[12px] flex-shrink-0">{c.name.slice(0, 2).toUpperCase()}</div>
              <div className="flex-1">
                <div className="font-semibold text-[14px]">{c.name}</div>
                <div className="text-[12px] text-slate-400">{c.industry}</div>
              </div>
              <button onClick={() => decide("contractor", c.id, "approve")} data-testid={`approve-contractor-${c.id}`} className="px-3.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[12.5px] font-semibold">Approve</button>
              <button onClick={() => decide("contractor", c.id, "decline")} data-testid={`decline-contractor-${c.id}`} className="px-3.5 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Decline</button>
            </div>
          ))}
          {contractors.length === 0 && <div className="text-slate-400 text-sm text-center py-10">No contractor sign-ups pending.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
